// batch-68 gespreksvoorbereiding (FO-346) - het feitelijke dossierbeeld bij een
// HR-gesprek. Gedeelde DTO's (API -> backoffice/PDF) en request-schemas.
//
// KERNREGEL: feiten, geen oordelen. Deze vormen dragen aantallen, data, duur en
// verdelingen. Er is bewust geen veld voor een kwalificatie, score, rangschikking
// of teamvergelijking. Een kleur (`toon`) bestaat alleen voor certificaten
// (harde eis, geen oordeel over de persoon); alle andere kerncijfers zijn NEUTRAAL.

import { z } from 'zod'
import { queryBoolean } from './zod-query'

// ─── Rubrieken ───────────────────────────────────────────────────────────────

/**
 * De rubrieken, in de vaste volgorde van FO-346 §3.3 t/m §3.10; batch-71 (FO-537 §3.A3)
 * voegt de rubriek CONTROLE toe: de controlelijsten van voertuig, telefoon en gereedschap.
 */
export const GV_RUBRIEKEN = [
  'INZET',
  'PLANNING',
  'VERZUIM',
  'VEILIGHEID',
  'ONTWIKKELING',
  'GESPREKKEN',
  'NOTITIES',
  'POSITIEF',
  'CONTROLE',
] as const
export type GvRubriekKey = (typeof GV_RUBRIEKEN)[number]

export const GV_RUBRIEK_LABELS: Record<GvRubriekKey, string> = {
  INZET: 'Inzet',
  PLANNING: 'Planning en administratie',
  VERZUIM: 'Verzuim en aanwezigheid',
  VEILIGHEID: 'Veiligheid en compliance',
  ONTWIKKELING: 'Ontwikkeling',
  GESPREKKEN: 'Eerdere gesprekken',
  NOTITIES: 'HR-notities',
  POSITIEF: 'Positieve signalen',
  CONTROLE: 'Controle middelen',
}

/** Vaste teksten (één bron voor scherm en PDF). */
export const GV_TEKST_NIET_BESCHIKBAAR = 'Niet beschikbaar met jouw rechten'

/**
 * Zinnen over de rechten van de kijker. In het feitenoverzicht voor de medewerker gaat
 * het om de rechten van de maker, niet van de lezer: `maakGvMedewerkerVersie` vervangt
 * ze door de neutrale tegenhanger. De services gebruiken deze constanten (nooit een
 * eigen formulering), zodat de vervanging altijd raakt; de drift-guard bewaakt dat.
 */
export const GV_ZIN_NIET_MET_JOUW_RECHTEN = 'zijn niet zichtbaar met jouw rechten'
export const GV_ZIN_ALLEEN_MET_VERZUIMRECHTEN = 'zijn alleen zichtbaar met verzuimrechten'
export const GV_ZIN_CORRECTIES_ZONDER_RECHT = 'Uren-correcties van de verloning tellen alleen mee met het recht op de verloning.'
export const GV_ZIN_INITIATIEF_ZONDER_RECHT = 'Onderdelen waarvoor je geen recht hebt tellen niet mee en staan niet in de opsomming.'
export const GV_MEDEWERKER_ZINNEN: ReadonlyArray<readonly [string, string]> = [
  [GV_ZIN_NIET_MET_JOUW_RECHTEN, 'zijn niet opgenomen in dit overzicht'],
  [GV_ZIN_ALLEEN_MET_VERZUIMRECHTEN, 'zijn niet opgenomen in dit overzicht'],
  [GV_ZIN_CORRECTIES_ZONDER_RECHT, 'Uren-correcties van de verloning zijn niet opgenomen in dit overzicht.'],
  [GV_ZIN_INITIATIEF_ZONDER_RECHT, 'Onderdelen die niet in dit overzicht zijn opgenomen, tellen niet mee en staan niet in de opsomming.'],
]
export const GV_TEKST_LEEG = 'Geen gegevens in deze periode'
export const GV_TEKST_BENADERING = 'benadering'
export const GV_TOELICHTING_TIJDEN =
  'Tijden zijn zoals de medewerker ze registreerde; ze zijn niet gecontroleerd tegen GPS of de klant.'
export const GV_TOELICHTING_FEITEN =
  'Dit overzicht toont feiten: aantallen, data, duur en verdelingen. Het geeft geen oordeel; de interpretatie hoort in het gesprek.'

/**
 * De verzuimverdeling (`GV_VERZUIM_VERDELING_KEYS`) is een kale telling: zonder label,
 * kleur of norm (FO-346 §3.5), dus ook zonder het slot en het kader van een intern
 * onderdeel. Dat ze niet in het feitenoverzicht voor de medewerker staat (§3.11), zegt
 * deze ene neutrale zin op rubriekniveau, in paneel en PDF.
 */
export const GV_ZIN_VERDELING_NIET_IN_FEITENOVERZICHT =
  'De eerste ziektedag per weekdag en de meldingen aansluitend op een vrije dag staan niet in het feitenoverzicht voor de medewerker.'

/**
 * "Planning niet gevolgd" (FO-346 §3.4): de stand van de reden op een dag met een
 * afwijking. De app vraagt alleen om een reden boven zijn eigen drempel
 * (`requiresUrenAfwijkingReden` in `@streep/utils`, dezelfde regel als portal en API).
 * Daaronder is "geen reden" dus geen ontbrekende reden; deze labels houden dat uit elkaar.
 */
export const GV_REDEN_GEVRAAGD_NIET_INGEVULD = 'Reden gevraagd, niet ingevuld'
export const GV_REDEN_NIET_GEVRAAGD = 'Geen reden gevraagd (onder de drempel voor een reden)'

// ─── Periode ─────────────────────────────────────────────────────────────────

export const GV_PERIODE_KEUZES = ['SINDS_VORIG_GESPREK', '3M', '6M', '12M', 'VRIJ'] as const
export type GvPeriodeKeuze = (typeof GV_PERIODE_KEUZES)[number]

export const GV_PERIODE_KEUZE_LABELS: Record<GvPeriodeKeuze, string> = {
  SINDS_VORIG_GESPREK: 'Sinds vorig gesprek',
  '3M': '3 mnd',
  '6M': '6 mnd',
  '12M': '12 mnd',
  VRIJ: 'Vrij',
}

/** Langste vrij te kiezen periode (begrenst de laadtijd; ruim genoeg voor een exitgesprek). */
export const GV_MAX_PERIODE_MAANDEN = 36

const YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/
const ymd = (melding = 'Kies een geldige datum') => z.string().regex(YMD_REGEX, melding)

/** Query van `GET /employees/:id/gespreksvoorbereiding` (en de PDF-variant). */
export const gespreksvoorbereidingQuerySchema = z
  .object({
    periode: z.enum(GV_PERIODE_KEUZES).optional(),
    van: ymd().optional(),
    tot: ymd().optional(),
    gesprekId: z.string().trim().min(1).max(64).optional(),
    vernieuwen: queryBoolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.periode === 'VRIJ') {
      if (!v.van) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['van'], message: 'Kies een begindatum' })
      if (!v.tot) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tot'], message: 'Kies een einddatum' })
    }
    if (v.van && v.tot && v.van > v.tot) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tot'], message: 'De einddatum ligt voor de begindatum' })
    }
  })
export type GespreksvoorbereidingQuery = z.infer<typeof gespreksvoorbereidingQuerySchema>

// ─── Waarden, cellen, markeringen ────────────────────────────────────────────

/** Een getoonde waarde. De client formatteert (uren met 2 decimalen of u:mm). */
export type GvWaarde =
  | { soort: 'AANTAL'; aantal: number }
  | { soort: 'UREN'; uren: number }
  | { soort: 'DAGEN'; dagen: number }
  | { soort: 'MINUTEN'; minuten: number }
  | { soort: 'KM'; km: number }
  | { soort: 'X_VAN_Y'; x: number; y: number }
  | { soort: 'TEKST'; tekst: string }

/**
 * BENADERING: het feit rust op een terugvaltijdstip (bv. het laatste
 * wijzigingsmoment) omdat het echte moment niet wordt vastgelegd. Altijd
 * zichtbaar in scherm en PDF; nooit stil als feit gepresenteerd.
 */
export type GvMarkering = 'BENADERING'

/** Kleur van een kerncijfer of regel. Alleen certificaten mogen ROOD/ORANJE. */
export type GvToon = 'NEUTRAAL' | 'ROOD' | 'ORANJE'

export interface GvVergelijking {
  /** De waarde over de eigen vorige periode van gelijke lengte. */
  vorige: GvWaarde
  /** Neutrale richting (pijl in neutrale kleur); geen oordeel. */
  richting: 'HOGER' | 'LAGER' | 'GELIJK'
}

export interface GvKerncijfer {
  key: string
  label: string
  waarde: GvWaarde
  /** Feitelijke aanvulling onder het getal (bv. "van 61 werkdagen"). */
  sub: string | null
  /** Definitie en drempel, voor de uitleg bij het feit. */
  uitleg: string
  /** null = geen vergelijking (bv. korter in dienst dan de periode). */
  vergelijking: GvVergelijking | null
  markering: GvMarkering | null
  /** Verwijst naar een detaillijst in dezelfde rubriek ("Toon details"). */
  detailKey: string | null
  toon: GvToon
}

export type GvCel =
  | { t: 'TEKST'; v: string }
  | { t: 'DATUM'; v: string }
  | { t: 'DATUMTIJD'; v: string }
  | { t: 'UREN'; v: number }
  | { t: 'AANTAL'; v: number }
  | { t: 'MINUTEN'; v: number }
  | { t: 'LEEG' }

export interface GvRij {
  cellen: GvCel[]
  /** Directe link naar de bron (werkbon, ziekmelding, certificaat, gesprek, ...). */
  href: string | null
  markering: GvMarkering | null
  toon: GvToon
}

export interface GvDetailLijst {
  key: string
  titel: string
  kolommen: string[]
  rijen: GvRij[]
  leegTekst: string
  toelichting: string | null
}

export interface GvStaaf {
  label: string
  waarde: number
  /** Referentielijn per staaf (bv. contracturen van die week); null = geen. */
  referentie: number | null
}

export interface GvNotitieItem {
  id: string
  datum: string
  categorieLabel: string
  auteurNaam: string
  auteurRolLabel: string | null
  aangemaaktOp: string
  /** Alleen gevuld als de notitie na het aanmaken is gewijzigd. */
  gewijzigdOp: string | null
  inhoud: string
  inPeriode: boolean
  href: string
}

export type GvBlok =
  | {
      soort: 'STAAFJES'
      key: string
      titel: string
      eenheid: 'UREN' | 'AANTAL'
      staven: GvStaaf[]
      referentieLabel: string | null
      uitleg: string | null
    }
  | {
      soort: 'LIJST'
      key: string
      titel: string
      kolommen: string[]
      rijen: GvRij[]
      leegTekst: string
      uitleg: string | null
      detailKey: string | null
    }
  | {
      soort: 'NOTITIES'
      key: string
      titel: string
      vertrouwelijk: boolean
      notities: GvNotitieItem[]
      leegTekst: string
    }
  | { soort: 'TEKST'; key: string; titel: string; tekst: string }
  /** Een deel van een rubriek waarvoor de kijker geen recht heeft: geen data, geen telling. */
  | { soort: 'NIET_BESCHIKBAAR'; key: string; titel: string }

export type GvRubriek =
  | { key: GvRubriekKey; titel: string; beschikbaar: false }
  | {
      key: GvRubriekKey
      titel: string
      beschikbaar: true
      /** true = in deze periode geen enkel gegeven ("Geen gegevens in deze periode"). */
      leeg: boolean
      kerncijfers: GvKerncijfer[]
      blokken: GvBlok[]
      details: GvDetailLijst[]
      toelichtingen: string[]
      bron: { label: string; href: string } | null
    }

/** Eén feit in de strook "in één oogopslag": een waarde plus een feitelijk label. */
export interface GvChip {
  rubriek: GvRubriekKey
  waarde: GvWaarde
  /** Volgt de waarde, bv. "u gewerkt" of "ziekmeldingen (5 dagen)". */
  label: string
}

/** Rubriek-onderdelen die NIET in de versie voor de medewerker horen (FO-346 §3.11). */
export const GV_VERZUIM_VERDELING_KEYS = ['verdeling-weekdag', 'aansluitend'] as const

/**
 * Onderdelen (kerncijfers, blokken of detaillijsten, in elke rubriek) die evenmin
 * in de versie voor de medewerker horen: de vertrouwelijke notities en hun telling
 * (zelfs een aantal is een hint), de telling van verzuimnotities met een link naar
 * het interne dossier, interne beoordelingen die HR niet met de medewerker deelde
 * (en de telling daarvan), de klantbeoordelingen (die ziet de medewerker in de
 * app ook niet, ook niet als positieve waardering) en de ontwikkeldoelen en
 * doorgroeiwens die als "alleen intern" zijn vastgelegd. Daarnaast valt elke
 * sleutel met de prefix `GV_INTERN_PREFIX` weg.
 */
export const GV_MEDEWERKER_VERSIE_WEGLATEN = [
  'vertrouwelijk',
  'notities-vertrouwelijk',
  'verzuimnotities',
  'beoordelingen-niet-gedeeld',
  'beoordelingen-aantal',
  'klantbeoordelingen',
  'waardering-klant',
  'ontwikkeldoelen-intern',
  'doorgroeiwens-intern',
] as const

/** Een sleutel met deze prefix is intern en gaat nooit mee naar de medewerker. */
export const GV_INTERN_PREFIX = 'intern-'

/**
 * Onderdelen die WEL in de versie voor de medewerker horen. Samen met
 * `GV_MEDEWERKER_VERSIE_WEGLATEN`, `GV_VERZUIM_VERDELING_KEYS` en de prefix
 * `GV_INTERN_PREFIX` is elke sleutel van de services bewust ingedeeld: de
 * drift-guard (`services/gespreksvoorbereiding/__tests__/medewerker-versie-drift.test.ts`
 * in de api) faalt op een nieuwe sleutel die nergens staat. Een sleutel met een
 * variabel deel staat hier als prefix (eindigt op een koppelteken).
 */
export const GV_MEDEWERKER_VERSIE_TOEGESTAAN = [
  // Inzet
  'gewerkt',
  'gewerkte-dagen',
  'klanten',
  'overuren',
  'overuren-per-week',
  'uren-per-week',
  'weken-boven-contract',
  'weekenden',
  'weekend-nacht',
  'reisuren',
  'tvt',
  'oproepen',
  'inzet-signalen',
  // Planning
  'gepland-gewerkt',
  'later-begonnen',
  'eerder-gestopt',
  'afwijking-gepland',
  'wijkt-af',
  'planning-niet-gevolgd',
  'bon-ontbreekt',
  'te-laat-ingevuld',
  'achteraf-aangepast',
  'afkeuringen',
  'correcties',
  // Verzuim
  'meldingen',
  'meldingen-12m',
  'verzuimdagen',
  'melding-na-aanvang',
  'bijzonder-verlof',
  // Veiligheid
  'certificaten',
  'certificaten-behaald',
  'incidenten',
  'onveilig-gemeld',
  'lmra-tra',
  'pbm-afwijkingen',
  'toolbox',
  'checklists',
  // Gesprekken
  'laatste-per-type',
  'actiepunten',
  'afspraken-tekst-',
  'open-actiepunten',
  'laatste-gesprek',
  'gesprekken-periode',
  'beoordelingen',
  // Ontwikkeling
  'opleidingen',
  'opleidingen-gevolgd',
  'opleidingen-open',
  'ontwikkeldoelen',
  'ontwikkelpad',
  'doorgroeiwens',
  // batch-71 gesprek-secties (FO-536 §3.C7) - opvolging; de interne inschatting staat onder GV_INTERN_PREFIX.
  'ontwikkeldoelen-verlopen',
  'skills-doelniveau',
  'ontwikkeling-vorig-gesprek',
  // batch-71 gesprek-secties (FO-537 §3.A3) - nooit een code.
  'controle-laatste',
  'controle-dit-gesprek',
  'controle-open-actiepunten',
  'controle-lijsten',
  'controle-niet-uitgevoerd',
  // Positief
  'extra-diensten',
  'flexibiliteit',
  'initiatief',
  'waardering',
  // Notities
  'notities',
  'algemeen',
] as const

// ─── Kop ─────────────────────────────────────────────────────────────────────

export interface GvKop {
  employeeId: string
  naam: string
  functie: string | null
  bv: string | null
  team: string | null
  inDienstSinds: string | null
  uitDienstOp: string | null
  contractUrenPerWeek: number | null
  /** Klanten rond het einde van de periode; alleen met het recht op uren. */
  huidigeKlanten: { beschikbaar: false } | { beschikbaar: true; klanten: Array<{ id: string; naam: string }> }
  /** Laatste afgeronde gesprek per type (verzuimgesprek neutraal zonder recht). */
  laatsteGesprekken: Array<{ gesprekId: string; typeLabel: string; datum: string; href: string }>
}

export interface GvGesprekContext {
  id: string
  titel: string
  geplandOp: string
  status: string
}

// ─── Drempels (HRM-instellingen) ─────────────────────────────────────────────

export interface GvDrempels {
  /** Later begonnen dan gepland: meer dan N minuten na de geplande begintijd. */
  laterBegonnenMinuten: number
  /** Eerder gestopt dan gepland: meer dan N minuten voor de geplande eindtijd. */
  eerderGestoptMinuten: number
  /** Afwijking van de planning: |gewerkt - gepland| groter dan N uur. */
  afwijkingUren: number
  /** Een bon is op tijd ingevuld tot het einde van de N-de werkdag na de werkdag. */
  bonDeadlineWerkdagen: number
  /** Kort verzuim: hooguit N kalenderdagen. */
  kortVerzuimDagen: number
  /** Extra dienst: toegewezen hooguit N uur voor aanvang. */
  korteTermijnUren: number
  /** Nachtdienst: meer dan N procent van de uren tussen 22:00 en 06:00. */
  nachtAandeelProcent: number
  /** Positieve beoordeling: score (1-5) vanaf deze waarde. */
  positieveBeoordelingVanaf: number
  /** Standaardperiode in maanden. */
  standaardPeriodeMaanden: number
  /** Alleen-lezen: komt uit de certificaat-instellingen (grootste waarschuwingstermijn). */
  certificaatBinnenkortDagen: number
}

export const GV_DREMPELS_DEFAULT: Omit<GvDrempels, 'certificaatBinnenkortDagen'> = {
  laterBegonnenMinuten: 15,
  eerderGestoptMinuten: 15,
  afwijkingUren: 1,
  bonDeadlineWerkdagen: 1,
  kortVerzuimDagen: 7,
  korteTermijnUren: 48,
  nachtAandeelProcent: 50,
  positieveBeoordelingVanaf: 4,
  standaardPeriodeMaanden: 3,
}

/** Sleutel in de generieke `SystemSetting`-tabel. */
export const GV_INSTELLING_KEY = 'gespreksvoorbereiding.drempels'

/** PUT-body van de drempels (de certificaatdrempel staat in de certificaat-instellingen). */
export const gvDrempelsSchema = z.object({
  laterBegonnenMinuten: z.number().int().min(1, 'Minimaal 1 minuut').max(240, 'Maximaal 240 minuten'),
  eerderGestoptMinuten: z.number().int().min(1, 'Minimaal 1 minuut').max(240, 'Maximaal 240 minuten'),
  afwijkingUren: z.number().min(0.25, 'Minimaal 0,25 uur').max(12, 'Maximaal 12 uur'),
  bonDeadlineWerkdagen: z.number().int().min(0, 'Minimaal 0 werkdagen').max(10, 'Maximaal 10 werkdagen'),
  kortVerzuimDagen: z.number().int().min(1, 'Minimaal 1 dag').max(42, 'Maximaal 42 dagen'),
  korteTermijnUren: z.number().int().min(1, 'Minimaal 1 uur').max(336, 'Maximaal 336 uur'),
  nachtAandeelProcent: z.number().int().min(1, 'Minimaal 1 procent').max(100, 'Maximaal 100 procent'),
  positieveBeoordelingVanaf: z.number().int().min(1, 'Minimaal 1').max(5, 'Maximaal 5'),
  standaardPeriodeMaanden: z.number().int().min(1, 'Minimaal 1 maand').max(12, 'Maximaal 12 maanden'),
})
export type GvDrempelsInput = z.infer<typeof gvDrempelsSchema>

// ─── Het samengestelde overzicht ─────────────────────────────────────────────

export interface GespreksvoorbereidingDTO {
  employeeId: string
  employeeNaam: string
  periode: { van: string; tot: string; keuze: GvPeriodeKeuze; dagen: number }
  /** Feitelijke meldingen over de periode (bv. "In dienst sinds ..."). */
  periodeMeldingen: string[]
  /** De eigen vorige periode van gelijke lengte; null = vergelijking vervalt. */
  vorigePeriode: { van: string; tot: string } | null
  /** Verzuimfrequentie loopt altijd over de laatste 12 maanden tot `periode.tot`. */
  verzuimTwaalfMaanden: { van: string; tot: string }
  gesprek: GvGesprekContext | null
  kop: GvKop
  feitenstrook: GvChip[]
  rubrieken: GvRubriek[]
  drempels: GvDrempels
  toelichting: { feiten: string; tijden: string }
  gegenereerdOp: string
  rechten: { snapshotVastleggen: boolean; instellingenBeheren: boolean }
  /** true = de versie voor de medewerker (zonder de interne onderdelen, zie `GV_MEDEWERKER_VERSIE_WEGLATEN`). */
  medewerkerVersie: boolean
}

// ─── Vastleggen bij het gesprek ──────────────────────────────────────────────

/**
 * Tag op de Documents van een vastlegging (het PDF en de bijlage voor de
 * medewerker): zo staan ze niet tussen de gespreksverslagen (ook EVALUATIE).
 */
export const GV_DOCUMENT_TAG = 'gespreksvoorbereiding'

export const gvSnapshotSchema = z
  .object({
    periode: z.enum(GV_PERIODE_KEUZES).optional(),
    van: ymd().optional(),
    tot: ymd().optional(),
    /** Maak ook de versie voor de medewerker (zonder de interne onderdelen, zie `GV_MEDEWERKER_VERSIE_WEGLATEN`). */
    bijlageVoorMedewerker: z.boolean().default(false),
    /** Bij een gesprek met meerdere deelnemers: over wie gaat de vastlegging. */
    employeeId: z.string().trim().min(1).max(64).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.periode === 'VRIJ' && (!v.van || !v.tot)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['van'], message: 'Kies een begin- en einddatum' })
    }
    if (v.van && v.tot && v.van > v.tot) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tot'], message: 'De einddatum ligt voor de begindatum' })
    }
  })
export type GvSnapshotInput = z.infer<typeof gvSnapshotSchema>

export interface GespreksvoorbereidingSnapshotDTO {
  id: string
  gesprekId: string | null
  periodeVan: string
  periodeTot: string
  rubrieken: GvRubriekKey[]
  aangemaaktDoorNaam: string
  createdAt: string
  /** Document + versie om via de same-origin download-proxy te openen. */
  pdf: { documentId: string; versionNr: number } | null
  medewerkerVersie: {
    documentId: string
    versionNr: number
    vrijgegevenOp: string | null
  } | null
  /** De PDF is alleen voor de maker (en een beheerder) te openen. */
  magOpenen: boolean
  /** Vrijgeven aan de medewerker: alleen de maker. */
  magVrijgeven: boolean
}
